import type { ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from './cx';

type C1TileTone = 'default' | 'warning' | 'critical';

export interface C1TileTrend {
  /**
   * The signed change since the comparison period.
   *
   * Null means there is nothing to compare against — a first term, a cohort with no
   * history — which is a different fact from "no change" and renders as an absence
   * rather than a flat zero.
   */
  delta: number | null;
  /** Appended to the figure, e.g. '%' or 'pts'. */
  unit?: string;
  /** What the change is measured against, e.g. 'vs last term'. */
  comparison?: ReactNode;
  /**
   * Which direction is the good one.
   *
   * DIRECTION AND SENTIMENT ARE TWO FACTS AND MUST NOT SHARE ONE PROP. A falling
   * absence count and a falling collection rate both point down; one is good news
   * and one is bad. The arrow follows the sign of `delta`, the colour follows this.
   * 'neither' is for figures where movement carries no verdict — a headcount is
   * neither good nor bad for going up — and leaves the trend uncoloured.
   */
  goodWhen?: 'up' | 'down' | 'neither';
}

interface C1TileProps {
  label: ReactNode;
  value: ReactNode;
  /** Qualifier beside the value, e.g. "/ 4" or "3 pending". */
  sub?: ReactNode;
  /** Small decorative glyph shown beside the label. */
  icon?: ReactNode;
  /**
   * Colours the figure. Reserved for states needing attention — the label must
   * carry the same meaning in words, so colour is reinforcement, not the message.
   */
  tone?: C1TileTone;
  /** Movement since a previous period, drawn under the figure. */
  trend?: C1TileTrend;
  /**
   * Recent history behind the figure, oldest first, as a bare sparkline.
   *
   * DECORATIVE BY CONSTRUCTION: no axis, no scale, no labels, and hidden from
   * assistive technology. It shows shape, not values — anything a reader must be
   * able to read off belongs in a real chart with an axis. `null` breaks the line
   * rather than being interpolated, for the same reason it does in TrendLine: a
   * period nobody measured must not be drawn as a smooth passage through it.
   */
  sparkline?: Array<number | null>;
  /** Makes the whole tile a link, so middle-click and "open in new tab" work. */
  to?: LinkProps['to'];
  /**
   * External destination (EduCore, etc.). Prefer `to` for in-app routes.
   * When both are set, `to` wins — an in-app path must never be overridden by a
   * misconfigured absolute URL.
   */
  href?: string;
  className?: string;
}

const SPARK_WIDTH = 100;
const SPARK_HEIGHT = 28;

/**
 * The sparkline path, as separate runs so a gap stays a gap.
 *
 * Drawn in a fixed 100×28 viewBox and stretched by CSS. That would normally smear
 * the stroke, which is why the charts measure their width instead — but a sparkline
 * has no text and only one line, so `vector-effect="non-scaling-stroke"` holds the
 * stroke at its stated width and there is nothing else left to distort.
 */
function sparkPaths(points: Array<number | null>): string[] {
  const measured = points.filter((point): point is number => point !== null);
  if (measured.length < 2) return [];

  const min = Math.min(...measured);
  const max = Math.max(...measured);
  const span = max - min || 1;
  const step = SPARK_WIDTH / Math.max(points.length - 1, 1);

  const runs: string[] = [];
  let current: string[] = [];

  points.forEach((point, index) => {
    if (point === null) {
      if (current.length > 1) runs.push(current.join(' '));
      current = [];
      return;
    }
    const x = index * step;
    const y = SPARK_HEIGHT - ((point - min) / span) * SPARK_HEIGHT;
    current.push(`${current.length === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`);
  });
  if (current.length > 1) runs.push(current.join(' '));

  return runs;
}

function TrendMark({ trend }: { trend: C1TileTrend }) {
  const { delta, unit = '', comparison, goodWhen = 'neither' } = trend;

  if (delta === null) {
    return (
      <span className="c1-tileTrend">
        <span className="c1-tileTrendValue">—</span>
        {comparison ? <span className="c1-tileTrendNote">{comparison}</span> : null}
      </span>
    );
  }

  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const sentiment =
    direction === 'flat' || goodWhen === 'neither'
      ? 'neutral'
      : direction === goodWhen
        ? 'positive'
        : 'negative';

  // The sign is part of the text, not only the arrow: a screen reader reads
  // "+4.2% vs last term" and needs no separate word for the direction, and the
  // arrow can stay hidden instead of being announced as a shape.
  const sign = direction === 'up' ? '+' : direction === 'down' ? '\u2212' : '';
  const magnitude = `${sign}${Math.abs(delta)}${unit}`;

  return (
    <span className={cx('c1-tileTrend', `c1-tileTrend--${sentiment}`)}>
      <svg
        className="c1-tileTrendArrow"
        width="8"
        height="8"
        viewBox="0 0 8 8"
        aria-hidden="true"
        focusable="false"
      >
        {direction === 'up' && <path d="M4 0 8 6H0z" fill="currentColor" />}
        {direction === 'down' && <path d="M4 8 0 2h8z" fill="currentColor" />}
        {direction === 'flat' && <rect x="0" y="3" width="8" height="2" fill="currentColor" />}
      </svg>
      <span className="c1-tileTrendValue">{magnitude}</span>
      {comparison ? <span className="c1-tileTrendNote">{comparison}</span> : null}
    </span>
  );
}

/**
 * A headline figure with its label.
 *
 * Label first, figure second: the reader needs to know what they are looking at
 * before the number means anything, and a column of tiles scans by label.
 *
 * `trend` and `sparkline` are both optional and both off by default, so a tile
 * that only has a figure stays the plain figure it was.
 */
export function C1Tile({
  label,
  value,
  sub,
  icon,
  tone = 'default',
  trend,
  sparkline,
  to,
  href,
  className,
}: C1TileProps) {
  const spark = sparkline ? sparkPaths(sparkline) : [];

  const content = (
    <>
      <span className="c1-tileHead">
        {icon ? (
          <span className="c1-tileIcon" aria-hidden="true">
            {icon}
          </span>
        ) : null}
        <span className="c1-tileLabel">{label}</span>
      </span>
      <span className="c1-tileFigure">
        <span className={cx('c1-tileValue', tone !== 'default' && `c1-tileValue--${tone}`)}>
          {value}
        </span>
        {sub ? <span className="c1-tileSub">{sub}</span> : null}
      </span>
      {spark.length > 0 && (
        <svg
          className="c1-tileSpark"
          viewBox={`0 0 ${SPARK_WIDTH} ${SPARK_HEIGHT}`}
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        >
          {spark.map((path) => (
            <path
              key={path}
              d={path}
              fill="none"
              stroke="currentColor"
              strokeWidth={1.5}
              strokeLinecap="round"
              strokeLinejoin="round"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
      )}
      {trend ? <TrendMark trend={trend} /> : null}
    </>
  );

  if (to !== undefined) {
    return (
      <Link to={to} className={cx('c1-tile', 'c1-tile--link', className)}>
        {content}
      </Link>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={cx('c1-tile', 'c1-tile--link', className)}
      >
        {content}
      </a>
    );
  }

  return <div className={cx('c1-tile', className)}>{content}</div>;
}
